import type { MetricItemDTO } from '@/types/agentEvents';

export interface MetricsBarChartProps {
  /** Metric items with intervalStartTime (from non-TOTAL period). */
  data: MetricItemDTO[];
  /** Parse value to number for bar height. Default: number or parseFloat. */
  valueToNumber?: (value: number | string) => number;
  /** Format value for tooltip/label. Default: String(value). */
  valueFormatter?: (value: number | string) => string;
  /** Bar color. Default: blue. */
  barClassName?: string;
  /** Chart height in pixels. Default: 200. */
  height?: number;
}

const defaultValueToNumber = (value: number | string): number => {
  if (typeof value === 'number') return value;
  const n = parseFloat(String(value).replace(/%/g, ''));
  return Number.isFinite(n) ? n : 0;
};

function formatIntervalLabel(intervalStartTime: string): string {
  const d = new Date(intervalStartTime);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getHours() !== 0 || d.getMinutes() !== 0
      ? { hour: '2-digit', minute: '2-digit' }
      : {}),
  });
}

export function MetricsBarChart({
  data,
  valueToNumber = defaultValueToNumber,
  valueFormatter = v => String(v),
  barClassName = 'bg-blue-500',
  height = 200,
}: MetricsBarChartProps) {
  const sorted = [...data].sort(
    (a, b) =>
      new Date(a.intervalStartTime ?? 0).getTime() -
      new Date(b.intervalStartTime ?? 0).getTime()
  );

  const values = sorted.map(item => valueToNumber(item.value));
  const maxValue = Math.max(...values, 1);

  return (
    <div className="w-full">
      <div
        className="flex items-end gap-1 w-full"
        style={{ height: `${height}px` }}
        role="img"
        aria-label="Bar chart of metrics over time"
      >
        {sorted.map((item, index) => {
          const num = values[index];
          const barHeight = (num / maxValue) * height;
          const label = item.intervalStartTime
            ? formatIntervalLabel(item.intervalStartTime)
            : '';
          return (
            <div
              key={item.id}
              className={`flex-1 min-w-0 rounded-t ${barClassName}`}
              style={{ height: `${Math.max(barHeight, 2)}px` }}
              title={`${label}: ${valueFormatter(item.value)}`}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-2">
        {sorted.length > 0 && sorted[0].intervalStartTime && (
          <span>{formatIntervalLabel(sorted[0].intervalStartTime)}</span>
        )}
        {sorted.length > 1 && sorted[sorted.length - 1].intervalStartTime && (
          <span>
            {formatIntervalLabel(
              sorted[sorted.length - 1].intervalStartTime ?? ''
            )}
          </span>
        )}
      </div>
    </div>
  );
}
